import type { Booking, CourtSportType, RepeatOption, PaymentStatus, BookingStatus } from '@/lib/types';
import { format, addDays, subDays, subMonths } from 'date-fns';
import { defaultOrganizationId } from './organizations';
import { sampleSpacesData, spaceIdMapping } from './spaces';

const todayString = format(new Date(), 'yyyy-MM-dd');

// User mappings for sample bookings
const adminUser_id = '2847391650';
const adminUser_name = "Roger Federer";
const mariaSharapova_id_mapped = '4051728396';
const mariaSharapova_name = "Maria Sharapova";
const cocoGauff_id_mapped = '2839506174';
const cocoGauff_name = "Coco Gauff";

// Helper function to get space details
const getSpaceDetailsForSample = (spaceOriginalId: string): { 
  spaceId: string, 
  spaceName?: string, 
  sport?: CourtSportType, 
  organizationId?: string, 
  clubId: string 
} => {
  const newNumericId = spaceIdMapping[spaceOriginalId];
  if (!newNumericId) {
    return { 
      spaceId: spaceOriginalId, 
      organizationId: defaultOrganizationId,
      clubId: '9876543210'
    };
  }
  const space = sampleSpacesData.find(s => s.id === newNumericId);
  return {
    spaceId: space?.id || newNumericId,
    spaceName: space ? `${space.businessId || space.id} - ${space.displayName}` : undefined,
    sport: space?.courtType,
    organizationId: space?.organizationId || defaultOrganizationId,
    clubId: space?.clubId || '9876543210',
  };
};

// Space details
const tennisSpace1_details = getSpaceDetailsForSample('T1');
const tennisSpace2_details = getSpaceDetailsForSample('T2');
const pickleballSpace1_details = getSpaceDetailsForSample('PB1');

// Helper function to generate historical bookings
const generateHistoricalBookings = (): Booking[] => {
  const historicalBookings: Booking[] = [];
  const users = [
    { id: adminUser_id, name: adminUser_name },
    { id: mariaSharapova_id_mapped, name: mariaSharapova_name },
    { id: cocoGauff_id_mapped, name: cocoGauff_name }
  ];
  const spaces = [tennisSpace1_details, tennisSpace2_details, pickleballSpace1_details];
  const timeSlots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
  
  // Generate bookings for the past 6 months
  for (let monthOffset = 0; monthOffset < 6; monthOffset++) {
    const monthDate = subMonths(new Date(), monthOffset);
    const bookingsPerMonth = 15 + Math.floor(Math.random() * 10); // 15-25 bookings per month
    
    for (let i = 0; i < bookingsPerMonth; i++) {
      const randomDay = Math.floor(Math.random() * 28) + 1; // Random day in month
      const bookingDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), randomDay);
      const user = users[Math.floor(Math.random() * users.length)];
      const space = spaces[Math.floor(Math.random() * spaces.length)];
      const startTime = timeSlots[Math.floor(Math.random() * timeSlots.length)];
      const startHour = parseInt(startTime.split(':')[0]);
      const duration = Math.random() > 0.5 ? 1 : 1.5; // 1 or 1.5 hours
      const endHour = startHour + duration;
      const endTime = `${Math.floor(endHour).toString().padStart(2, '0')}:${endHour % 1 === 0 ? '00' : '30'}`;
      
      // Generate a deterministic 10-digit numeric ID
      const numericId = (1000000000 + (monthOffset * 1000000) + (i * 1000) + (randomDay * 10)).toString();
      
      historicalBookings.push({
        id: numericId,
        userId: user.id,
        userUid: user.id,
        userName: user.name,
        ...space,
        sport: space.sport,
        date: format(bookingDate, 'yyyy-MM-dd'),
        startTime,
        endTime,
        title: `${user.name.split(' ')[0]} - Training`,
        priceAmount: 20 + Math.floor(Math.random() * 30),
        currency: 'CHF',
        paymentStatus: Math.random() > 0.2 ? 'Paid' : 'Pending' as PaymentStatus,
        repeat: 'None' as RepeatOption,
        numberOfPeople: Math.floor(Math.random() * 4) + 1,
        organizationId: defaultOrganizationId,
        tenantId: defaultOrganizationId,
        status: Math.random() > 0.1 ? 'Confirmed' : 'Completed' as BookingStatus,
        dateCreated: format(subDays(bookingDate, Math.floor(Math.random() * 7)), 'yyyy-MM-dd'),
        dateUpdated: format(subDays(bookingDate, Math.floor(Math.random() * 3)), 'yyyy-MM-dd')
      });
    }
  }
  
  return historicalBookings;
};

// Helper function to generate Roger Federer's historical bookings (past 6 months)
const generateRogerFedererHistoricalBookings = (): Booking[] => {
  const historicalBookings: Booking[] = [];
  const spaces = [tennisSpace1_details, tennisSpace2_details, pickleballSpace1_details];
  const timeSlots = ['08:00', '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
  
  // Generate bookings for the past 6 months - ensure Roger has consistent data
  for (let monthOffset = 0; monthOffset < 6; monthOffset++) {
    const monthDate = subMonths(new Date(), monthOffset);
    const bookingsPerMonth = 12 + Math.floor(Math.random() * 8); // 12-20 bookings per month for Roger
    
    for (let i = 0; i < bookingsPerMonth; i++) {
      const randomDay = Math.floor(Math.random() * 28) + 1; // Random day in month
      const bookingDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), randomDay);
      const space = spaces[Math.floor(Math.random() * spaces.length)];
      const startTime = timeSlots[Math.floor(Math.random() * timeSlots.length)];
      const startHour = parseInt(startTime.split(':')[0]);
      const duration = Math.random() > 0.3 ? 1.5 : 1; // Mostly 1.5 hour sessions
      const endHour = startHour + duration;
      const endTime = `${Math.floor(endHour).toString().padStart(2, '0')}:${endHour % 1 === 0 ? '00' : '30'}`;
      
      // Generate a proper 10-digit numeric ID for Roger Federer
      const rogerNumericId = (2000000000 + (monthOffset * 1000000) + (randomDay * 1000) + i).toString();
      
      const booking: Booking = {
        id: rogerNumericId,
        userId: adminUser_id,
        userUid: adminUser_id,
        userName: adminUser_name,
        ...space,
        sport: space.sport,
        date: format(bookingDate, 'yyyy-MM-dd'),
        startTime,
        endTime,
        title: `${adminUser_name.split(' ')[0]} - ${space.sport === 'Tennis' ? 'Tennis Practice' : space.sport === 'Pickleball' ? 'Pickleball Training' : 'Court Session'}`,
        priceAmount: space.sport === 'Tennis' ? 35 : space.sport === 'Pickleball' ? 30 : 25,
        currency: 'CHF',
        paymentStatus: Math.random() > 0.15 ? 'Paid' : 'Pending' as PaymentStatus,
        repeat: 'None' as RepeatOption,
        numberOfPeople: Math.floor(Math.random() * 3) + 1, // 1-3 people
        organizationId: defaultOrganizationId,
        tenantId: defaultOrganizationId,
        status: 'Completed' as BookingStatus, // Historical bookings are completed
        dateCreated: format(subDays(bookingDate, Math.floor(Math.random() * 7)), 'yyyy-MM-dd'),
        dateUpdated: format(subDays(bookingDate, Math.floor(Math.random() * 3)), 'yyyy-MM-dd')
      };
      
      historicalBookings.push(booking);
    }
  }
  
  return historicalBookings;
};

// Helper function to generate Maria Sharapova's recent bookings (past 2 weeks)
const generateMariaSharapovaRecentBookings = (): Booking[] => {
  const recentBookings: Booking[] = [];
  const spaces = [tennisSpace1_details, tennisSpace2_details, pickleballSpace1_details];
  const timeSlots = ['09:00', '10:30', '14:00', '15:30', '17:00'];
  
  // Generate 8-10 bookings for Maria in the past 2 weeks
  for (let dayOffset = 1; dayOffset <= 14; dayOffset++) {
    // Skip some days to make it realistic (not every day)
    if (dayOffset % 2 === 0 || dayOffset === 7 || dayOffset === 13) continue;
    
    const bookingDate = subDays(new Date(), dayOffset);
    const space = spaces[dayOffset % spaces.length];
    const startTime = timeSlots[dayOffset % timeSlots.length];
    const startHour = parseInt(startTime.split(':')[0]);
    const startMinute = parseInt(startTime.split(':')[1]);
    const duration = dayOffset % 3 === 0 ? 2 : 1.5; // Mix of 1.5 and 2 hour sessions
    const endHour = startHour + Math.floor(duration);
    const endMinute = startMinute + ((duration % 1) * 60);
    const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
    
    // Generate a deterministic 10-digit numeric ID for Maria Sharapova
    const mariaNumericId = (3000000000 + (dayOffset * 100000) + (startHour * 1000) + startMinute).toString();
    
    recentBookings.push({
      id: mariaNumericId,
      userId: mariaSharapova_id_mapped,
      userUid: mariaSharapova_id_mapped,
      userName: mariaSharapova_name,
      ...space,
      sport: space.sport,
      date: format(bookingDate, 'yyyy-MM-dd'),
      startTime,
      endTime,
      title: `Maria - ${space.sport === 'Tennis' ? 'Tennis Training' : 'Pickleball Class'}`,
      priceAmount: space.sport === 'Tennis' ? 45 : 35,
      currency: 'CHF',
      paymentStatus: dayOffset % 4 === 0 ? 'Pending' : 'Paid' as PaymentStatus,
      repeat: 'None' as RepeatOption,
      numberOfPeople: dayOffset % 3 === 0 ? 4 : 2, // Mix of private and group sessions
      organizationId: defaultOrganizationId,
      tenantId: defaultOrganizationId,
      status: 'Completed' as BookingStatus, // Past bookings are completed
      dateCreated: format(subDays(bookingDate, 2), 'yyyy-MM-dd'),
      dateUpdated: format(subDays(bookingDate, 1), 'yyyy-MM-dd')
    });
  }
  
  return recentBookings;
};

// Sample bookings data - includes historical data for analytics
export const sampleBookingsData: Booking[] = [
  ...generateHistoricalBookings(), // Add historical bookings for dashboard analytics
  ...generateRogerFedererHistoricalBookings(), // Add Roger Federer's dedicated historical bookings
  ...generateMariaSharapovaRecentBookings(), // Add Maria Sharapova's recent bookings
  // Today's bookings
  { 
    id: '4729381650', 
    userId: adminUser_id,
    ...tennisSpace1_details, 
    sport: tennisSpace1_details.sport,
    date: todayString, 
    startTime: '10:00', 
    endTime: '11:00', 
    userName: adminUser_name, 
    title: 'Practice Session', 
    userUid: adminUser_id, 
    priceAmount: 20, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    customFields: { "Notes": "Some notes for Laurent", "playerIds": JSON.stringify([adminUser_id]) }, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Confirmed' as BookingStatus,
    dateCreated: format(subDays(new Date(), 2), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 1), 'yyyy-MM-dd')
  },
  { 
    id: '8394726150', 
    userId: adminUser_id,
    ...tennisSpace1_details, 
    sport: tennisSpace1_details.sport,
    date: todayString, 
    startTime: '11:00', 
    endTime: '12:00', 
    userName: adminUser_name, 
    title: 'Friendly Match', 
    userUid: adminUser_id, 
    priceAmount: 20, 
    currency: 'CHF', 
    paymentStatus: 'Pending' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Pending' as BookingStatus,
    dateCreated: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    dateUpdated: format(new Date(), 'yyyy-MM-dd')
  },
  
  // Maria Sharapova bookings
  { 
    id: '1148675178', 
    userId: mariaSharapova_id_mapped,
    ...tennisSpace1_details, 
    sport: tennisSpace1_details.sport,
    date: format(addDays(new Date(), 1), 'yyyy-MM-dd'), 
    startTime: '14:00', 
    endTime: '15:30', 
    userName: mariaSharapova_name, 
    title: 'Maria - Tennis Training', 
    userUid: mariaSharapova_id_mapped, 
    priceAmount: 35, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    status: 'Confirmed' as BookingStatus, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    dateCreated: format(subDays(new Date(), 3), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 2), 'yyyy-MM-dd')
  },
  { 
    id: '2847395162', 
    userId: mariaSharapova_id_mapped,
    ...tennisSpace2_details, 
    sport: tennisSpace2_details.sport,
    date: format(addDays(new Date(), 3), 'yyyy-MM-dd'), 
    startTime: '10:00', 
    endTime: '11:30', 
    userName: mariaSharapova_name, 
    title: 'Maria - Teaching Session', 
    userUid: mariaSharapova_id_mapped, 
    priceAmount: 40, 
    currency: 'CHF', 
    paymentStatus: 'Pending' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    status: 'Pending' as BookingStatus, 
    numberOfPeople: 4, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    dateCreated: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 4), 'yyyy-MM-dd')
  },
  { 
    id: '3958472610', 
    userId: mariaSharapova_id_mapped,
    ...pickleballSpace1_details, 
    sport: pickleballSpace1_details.sport,
    date: format(addDays(new Date(), 5), 'yyyy-MM-dd'), 
    startTime: '16:00', 
    endTime: '17:00', 
    userName: mariaSharapova_name, 
    title: 'Maria - Pickleball Class', 
    userUid: mariaSharapova_id_mapped, 
    priceAmount: 30, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    status: 'Confirmed' as BookingStatus, 
    numberOfPeople: 6, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    dateCreated: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 6), 'yyyy-MM-dd')
  },
  
  // Additional bookings
  { 
    id: '5617283940', 
    userId: cocoGauff_id_mapped,
    ...tennisSpace2_details, 
    sport: tennisSpace2_details.sport,
    date: todayString, 
    startTime: '09:00', 
    endTime: '10:00', 
    userName: cocoGauff_name, 
    title: 'Coco - Practice', 
    userUid: cocoGauff_id_mapped, 
    priceAmount: 20, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Confirmed' as BookingStatus,
    dateCreated: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    dateUpdated: format(new Date(), 'yyyy-MM-dd')
  },
  { 
    id: '9283746510', 
    userId: adminUser_id,
    ...pickleballSpace1_details, 
    sport: pickleballSpace1_details.sport,
    date: format(addDays(new Date(), 1), 'yyyy-MM-dd'), 
    startTime: '09:00', 
    endTime: '10:00', 
    userName: adminUser_name, 
    title: 'Pickleball Game', 
    userUid: adminUser_id, 
    priceAmount: 25, 
    currency: 'CHF', 
    paymentStatus: 'Pending' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 4, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Pending' as BookingStatus,
    dateCreated: format(subDays(new Date(), 3), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 2), 'yyyy-MM-dd')
  },
  
  // Test bookings with known IDs for testing purposes
  { 
    id: '2000015004', // Known ID for testing
    userId: adminUser_id,
    ...tennisSpace1_details, 
    sport: tennisSpace1_details.sport,
    date: format(addDays(new Date(), 2), 'yyyy-MM-dd'), 
    startTime: '15:00', 
    endTime: '16:00', 
    userName: adminUser_name, 
    title: 'Roger - Test Booking', 
    userUid: adminUser_id, 
    priceAmount: 35, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Confirmed' as BookingStatus,
    dateCreated: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    dateUpdated: format(new Date(), 'yyyy-MM-dd')
  },
  { 
    id: '2000015005', // Another known ID for testing
    userId: adminUser_id,
    ...tennisSpace2_details, 
    sport: tennisSpace2_details.sport,
    date: format(addDays(new Date(), 3), 'yyyy-MM-dd'), 
    startTime: '16:00', 
    endTime: '17:30', 
    userName: adminUser_name, 
    title: 'Roger - Advanced Training', 
    userUid: adminUser_id, 
    priceAmount: 45, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 1, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Confirmed' as BookingStatus,
    dateCreated: format(subDays(new Date(), 2), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 1), 'yyyy-MM-dd')
  },
  { 
    id: '2000021000', // Specific ID that was being accessed in the URL
    userId: adminUser_id,
    ...tennisSpace1_details, 
    sport: tennisSpace1_details.sport,
    date: format(addDays(new Date(), 1), 'yyyy-MM-dd'), 
    startTime: '14:00', 
    endTime: '15:30', 
    userName: adminUser_name, 
    title: 'Roger - Championship Training', 
    userUid: adminUser_id, 
    priceAmount: 50, 
    currency: 'CHF', 
    paymentStatus: 'Paid' as PaymentStatus, 
    repeat: 'None' as RepeatOption, 
    numberOfPeople: 2, 
    organizationId: defaultOrganizationId, 
    tenantId: defaultOrganizationId,
    status: 'Confirmed' as BookingStatus,
    dateCreated: format(subDays(new Date(), 3), 'yyyy-MM-dd'),
    dateUpdated: format(subDays(new Date(), 1), 'yyyy-MM-dd')
  },
];

// Export helper functions
export const getBookingById = (id: string): Booking | undefined => {
  return sampleBookingsData.find(booking => booking.id === id);
};

export const getBookingsByUser = (userUid: string): Booking[] => {
  return sampleBookingsData.filter(booking => booking.userUid === userUid);
};

export const getBookingsByDate = (date: string): Booking[] => {
  return sampleBookingsData.filter(booking => booking.date === date);
}; 